import { useCookies } from "react-cookie";

import dayjs from "dayjs";
import {
  getAuth,
  signInWithEmailAndPassword,
  setPersistence,
  browserLocalPersistence
} from "firebase/auth";

import { fetchOurApi } from "../utils/apiCalls";

import {
  analyticsTrack,
  analyticsIdentify,
  triggerGoogleEvent
} from "../analytics";
import { referralPartners } from "../../components/account-access/Partners";

import { useAuth, signOut, useMutateAccountData } from "../../helpers/firebase";
import { getAccountId } from "../accountHelpers";

export const useLogout = () => {
  const [user] = useAuth();
  const { email } = user || {};
  const mutateAccountData = useMutateAccountData();

  return () => {
    const accountId = getAccountId();

    return signOut().then(() => {
      analyticsTrack("User Sign Out", { email });
      mutateAccountData(accountId);
      return window.sessionStorage.clear();
    });
  };
};

export const useReferralCookie = () => {
  const REFERRAL_COOKIE_NAME = "aclymate-referral-partner";
  const [cookies, setCookie] = useCookies([REFERRAL_COOKIE_NAME]);

  const referralCookie = cookies[REFERRAL_COOKIE_NAME];
  const referralPartner =
    referralPartners.find(({ slug }) => slug === referralCookie)?.name || "";

  const setReferralCookie = (slug) => {
    if (referralCookie) {
      return;
    }

    return setCookie(REFERRAL_COOKIE_NAME, slug, {
      path: "/",
      expires: dayjs().add(1, "year").toDate()
    });
  };

  return {
    setReferralCookie,
    referralPartner
  };
};

export const onSignInWithEmailAndPassword = async ({
  email,
  password,
  callback,
  setUserLoading,
  setErrorMsg
}) => {
  const auth = getAuth();

  await setPersistence(auth, browserLocalPersistence);

  return signInWithEmailAndPassword(auth, email, password)
    .then(({ user }) => {
      if (callback) {
        return callback(user);
      }
    })
    .catch((e) => {
      console.log("firebase error: ", e);
      if (setUserLoading) {
        setUserLoading(false);
      }
      if (e?.code === "auth/user-not-found") {
        return setErrorMsg("User not found. Please use the correct email.");
      }
      if (e?.code === "auth/wrong-password") {
        return setErrorMsg(
          "Incorrect password. Maybe try signing in with Google or Microsoft instead."
        );
      }

      return setErrorMsg("An error occured logging into your account.");
    });
};

export const onLoginSetSessionAndTracking = (user, accountId) => {
  const { email } = user || {};

  // Store only the plain account ID without collection prefix
  window.sessionStorage.setItem("accountId", accountId);
  analyticsIdentify(user);
  analyticsTrack("User Sign In", {
    accountId,
    email
  });
  return triggerGoogleEvent("login");
};

export const getUserAccountData = async ({ email, uid }) =>
  await fetchOurApi({
    path: "/onboarding/get-user-account-data",
    method: "POST",
    data: {
      email,
      uid
    },
    callback: (res) => res
  });

export const handleLoggedInUser = async ({
  user,
  setRedirect,
  setLoginLoading,
  setAccountAccessError,
  platformUrl = "platform/company/dashboard",
  allowNoAccountId = false
}) => {
  const { email } = user;

  if (setLoginLoading) {
    setLoginLoading(true);
  }

  const { companyId } = await user
    .getIdTokenResult(true)
    .then(({ claims: { company } }) => ({
      companyId: company
    }));

  const handleTokenIdDoesntExist = async () => {
    if (setLoginLoading) {
      setLoginLoading(false);
    }
    await signOut();
    return setAccountAccessError(
      "Account not found. Please try creating an account instead"
    );
  };

  if (!companyId && allowNoAccountId) {
    analyticsIdentify(user);
    analyticsTrack("User Sign In", { email });
    triggerGoogleEvent("login");
    return setRedirect(platformUrl);
  }

  if (!companyId) {
    return await handleTokenIdDoesntExist();
  }

  onLoginSetSessionAndTracking(user, companyId);

  return setRedirect(platformUrl);
};

export const sendExistingCompanyAdminEmail = async (companyData, userData) => {
  const { admins = [], id: companyId } = companyData || {};
  const { displayName, uid, email } = userData || {};

  return await Promise.all(
    admins.map(
      async ({ email: adminEmail }) =>
        await fetchOurApi({
          path: "/emails/send-auth-email",
          method: "POST",
          data: { adminEmail, companyId, displayName, uid, userEmail: email }
        })
    )
  );
};
